import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUnsavedChanges } from '@/components/shared/unsaved-changes'
import { FormFields } from '@/components/shared/form-field'
import { dialogClosePolicy } from '@/lib/unsaved-changes-policy'

const DialogDismissalContext = React.createContext({ busy: false, guarded: false })

interface DialogProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  dirty?: boolean
  busy?: boolean
  onDiscard?: () => void
}

/** Controlled save completion may close directly; user dismissal goes through this guard. */
export function Dialog({ open, defaultOpen = false, onOpenChange, dirty, busy = false, onDiscard, ...props }: DialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const isOpen = open ?? internalOpen
  const { confirmDiscard } = useUnsavedChanges(dirty ?? false, { enabled: isOpen, busy, onDiscard })
  const changeOpen = (nextOpen: boolean) => {
    const apply = () => {
      if (open === undefined) setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    }
    if (nextOpen) apply()
    else {
      const policy = dialogClosePolicy({ dirty: dirty ?? false, busy })
      if (policy === 'wait') return
      if (policy === 'confirm') confirmDiscard(apply)
      else apply()
    }
  }
  return <DialogDismissalContext.Provider value={{ busy, guarded: dirty !== undefined }}>
    <DialogPrimitive.Root open={isOpen} onOpenChange={changeOpen} {...props} />
  </DialogDismissalContext.Provider>
}
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogPortal = DialogPrimitive.Portal
export function DialogOverlay({ className, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  return <DialogPrimitive.Overlay className={cn('fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out', className)} {...props} />
}
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeDisabled?: boolean }
>(
  (
    {
      className,
      children,
      onPointerDownOutside,
      onInteractOutside,
      onEscapeKeyDown,
      closeDisabled = false,
      ...props
    },
    ref,
  ) => {
    const { busy, guarded } = React.useContext(DialogDismissalContext)
    const cannotClose = closeDisabled || busy
    return <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn('fixed left-1/2 top-1/2 z-50 grid max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-xl border bg-background p-6 shadow-xl', className)}
        onPointerDownOutside={(event) => {
          event.preventDefault()
          onPointerDownOutside?.(event)
        }}
        onInteractOutside={(event) => {
          event.preventDefault()
          onInteractOutside?.(event)
        }}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event)
          // Legacy editors keep their existing Escape behavior until explicitly guarded.
          if (cannotClose || !guarded) event.preventDefault()
        }}
        {...props}
      >
        <FormFields disabled={busy}>{children}</FormFields>
        <DialogPrimitive.Close
          aria-label="Close dialog"
          disabled={cannotClose}
          className="absolute right-4 top-4 rounded-md border border-transparent p-1.5 opacity-70 transition hover:border-border hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName
export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) { return <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} /> }
export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) { return <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} /> }
export const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({ className, ...props }, ref) => <DialogPrimitive.Title ref={ref} className={cn('text-lg font-semibold', className)} {...props} />)
DialogTitle.displayName = DialogPrimitive.Title.displayName
export const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({ className, ...props }, ref) => <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />)
DialogDescription.displayName = DialogPrimitive.Description.displayName
